import { OAuthReturnTarget } from "./oauthReturn";
import { withRequestDeadline } from "./requestDeadline";
import { User } from "firebase/auth";
import { getConnectionErrorMessage } from "../utils/connectionErrorMessages";

export async function beginWorkspaceOAuth(
  user: User,
  workspaceId: string,
  platform: string,
  options?: {
    signal?: AbortSignal;
    reconnectConnectionId?: string;
  } & OAuthReturnTarget
): Promise<never> {
  const body = await withRequestDeadline(
    async (signal) => {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE}/connections/connect-intent`,
        {
          method: "POST",
          signal,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "x-workspace-id": workspaceId,
          },
          body: JSON.stringify({
            platform,
            reconnectConnectionId: options?.reconnectConnectionId,
            returnPath: options?.returnPath,
            returnDraftId: options?.returnDraftId,
            returnIntent: options?.returnIntent,
          }),
        }
      ).catch(() => {
        // Shared by onboarding and Settings. A network failure happens before we
        // reach the provider, so don't present it as a denied permission.
        throw new TypeError(
          getConnectionErrorMessage("connection_network_error")
        );
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok || typeof body.url !== "string") {
        throw new Error(body.error || "Could not start account connection.");
      }
      return body;
    },
    15000,
    "Connecting is taking longer than expected. Try again or continue without connecting.",
    options?.signal
  );
  if (options?.signal?.aborted)
    throw new DOMException("Cancelled", "AbortError");
  window.location.assign(body.url);
  return new Promise<never>(() => undefined);
}
